import { SPECIALTY_TOKENS } from '@hc/shared';
import type { FacilitiesQuery } from '../api/client.js';
import { convertFromEur, CURRENCY_SYMBOL, type GlobalCurrency } from '../currency.js';
import { formatMoneyTrim } from '../format.js';

/** Format an EUR amount in the display currency, trailing zeros trimmed. */
const fmtRevenue = (v: number, ccy: GlobalCurrency): string =>
  formatMoneyTrim(convertFromEur(v, ccy), { currency: CURRENCY_SYMBOL[ccy] });

// Filter spec for the Facilities Map panel. Three multi-select categoricals
// (empty Set == "All") plus one revenue range.
//
// Revenue is EUR-based (not the Screener's £ turnover): the platform's
// universe is defined by turnover_eur >= €1M (PLATFORM_SCOPE_EUR_THRESHOLD),
// and the map only ever shows in-scope companies, so the slider floors at
// €1M and filters on the company's turnover_eur. Values display in €.

export type MapFilterKey = 'country' | 'subsegment' | 'ownershipType';

export const MAP_FILTER_KEYS: readonly MapFilterKey[] = ['country', 'subsegment', 'ownershipType'];

export interface MapFilterDef {
  key: MapFilterKey;
  label: string;
  /** Which facets list backs this filter's options. */
  facetKey: 'country' | 'subsegment' | 'ownershipType';
}

export const MAP_FILTER_DEFS: readonly MapFilterDef[] = [
  { key: 'country', label: 'Country', facetKey: 'country' },
  { key: 'subsegment', label: 'Sub-segment', facetKey: 'subsegment' },
  { key: 'ownershipType', label: 'Ownership type', facetKey: 'ownershipType' },
];

// Revenue range, in EUR. Floor = the €150k platform-scope threshold (at the
// floor the lower bound is "no cap"); ceiling >€100m. The slider runs over the
// shared non-linear REVENUE_STOPS ladder (see revenue-scale.ts).
export const MAP_REVENUE_MIN = 150_000;
export const MAP_REVENUE_MAX = 100_000_000;

export type RevenueRange = readonly [number, number];

export interface MapFilters {
  country: ReadonlySet<string>;
  subsegment: ReadonlySet<string>;
  ownershipType: ReadonlySet<string>;
  revenue: RevenueRange;
}

export function defaultMapFilters(): MapFilters {
  return {
    country: new Set<string>(),
    subsegment: new Set<string>(),
    ownershipType: new Set<string>(),
    revenue: [MAP_REVENUE_MIN, MAP_REVENUE_MAX],
  };
}

export function isMapRevenueActive(v: RevenueRange): boolean {
  return v[0] !== MAP_REVENUE_MIN || v[1] !== MAP_REVENUE_MAX;
}

export function countActiveMapFilters(f: MapFilters): number {
  let n = MAP_FILTER_KEYS.reduce((acc, k) => acc + (f[k].size > 0 ? 1 : 0), 0);
  if (isMapRevenueActive(f.revenue)) n += 1;
  return n;
}

// The slider operates in EUR internally (the €1M scope floor is exact and
// the API filters turnover_eur), but values DISPLAY in the workspace's
// global currency — €1M shown as ≈£0.87m / 11.2m kr / etc.

/** Slider edge label: ">X" at the ceiling, plain value otherwise. */
export function formatMapRevenueBound(v: number, edge: 'min' | 'max', ccy: GlobalCurrency): string {
  const f = fmtRevenue(v, ccy);
  if (edge === 'max' && v === MAP_REVENUE_MAX) return `>${f}`;
  return f;
}

/** Collapsed-row summary: "all" | "≤ X" | "≥ X" | "X – Y". */
export function summarizeMapRevenue(v: RevenueRange, ccy: GlobalCurrency): string {
  if (!isMapRevenueActive(v)) return 'all';
  const f = (n: number) => fmtRevenue(n, ccy);
  const lowerUnbounded = v[0] === MAP_REVENUE_MIN;
  const upperUnbounded = v[1] === MAP_REVENUE_MAX;
  if (lowerUnbounded && !upperUnbounded) return `≤ ${f(v[1])}`;
  if (!lowerUnbounded && upperUnbounded) return `≥ ${f(v[0])}`;
  return `${f(v[0])} – ${f(v[1])}`;
}

/** Translate the filters into the API query (omit empty / unbounded). */
export function buildFacilitiesQuery(f: MapFilters): FacilitiesQuery {
  const out: FacilitiesQuery = {};
  if (f.country.size) out.country = Array.from(f.country);
  // Sub-segment holds a MIX of canonical-17 labels and fine specialty tokens (one
  // hierarchical dropdown) — split back into the two API axes, exactly as the
  // Screener does. The API OR-s them, so a parent + child selection widens rather
  // than contradicts.
  if (f.subsegment.size) {
    const subs: string[] = [];
    const specs: string[] = [];
    for (const v of f.subsegment) (SPECIALTY_TOKENS.has(v) ? specs : subs).push(v);
    if (subs.length) out.subsegment = subs;
    if (specs.length) out.specialty = specs;
  }
  if (f.ownershipType.size) out.ownershipType = Array.from(f.ownershipType);
  const [lo, hi] = f.revenue;
  // At a bound, that edge is "no cap" — only send the active edge(s).
  if (lo !== MAP_REVENUE_MIN) out.turnoverEurMin = lo;
  if (hi !== MAP_REVENUE_MAX) out.turnoverEurMax = hi;
  return out;
}

/** Human label for a stored facet value. Ownership types come back as
 *  snake_case enum strings; title-case them. Country codes and the
 *  cross-market agent sub-segments are already display-ready. */
export function labelForFacet(key: MapFilterKey, value: string): string {
  if (key === 'ownershipType') {
    return value
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  return value;
}
